import { KeyboardEvent, useEffect, useRef, useState } from 'react';

/** Width of an element, kept current on resize (charts draw in real pixels so text never stretches). */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w] as const;
}

/** A round tick step (1, 2, 5 × 10ⁿ) for about `ticks` gridlines, and the first step at or above `v` as the axis max. */
function niceMax(v: number, ticks = 4) {
  const raw = Math.max(1, v) / ticks;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  return { max: Math.max(step, Math.ceil(Math.max(1, v) / step) * step), step };
}

const fmtDay = (d: string, opts: Intl.DateTimeFormatOptions) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, opts);

export interface DayPoint { date: string; washes: number; flagged: number }

/**
 * Washes per day as stacked columns: passed (chart-1) under flagged (chart-2), 2px surface gap between them.
 * Hover or arrow keys show one day's numbers; a table view sits underneath for keyboard/screen-reader use.
 */
export function DailyColumns({ points }: { points: DayPoint[] }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const plotH = 200, axisH = 26, left = 32, right = 4, top = 8;
  const { max, step } = niceMax(Math.max(...points.map((p) => p.washes), 0));
  const innerW = Math.max(0, width - left - right);
  const band = points.length ? innerW / points.length : 0;
  const colW = Math.max(3, Math.min(24, band * 0.62));
  const y = (v: number) => top + plotH - (v / max) * plotH;
  const labelEvery = Math.max(1, Math.ceil(44 / Math.max(band, 1)));
  const ticks = Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step);
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    setHover((h) => Math.min(points.length - 1, Math.max(0, (h ?? points.length - 1) + (e.key === 'ArrowRight' ? 1 : -1))));
  };
  const h = hover !== null ? points[hover] : null;

  return (
    <div ref={ref} className="gr-chart" onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={top + plotH + axisH} tabIndex={0} role="img" onKeyDown={onKey} onBlur={() => setHover(null)}
          aria-label={`Washes per day, ${points[0]?.date} to ${points[points.length - 1]?.date}. Use the arrow keys to read each day.`}>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={left} x2={width - right} y1={y(v)} y2={y(v)} className="gr-chart__grid" />
              <text x={left - 8} y={y(v) + 4} textAnchor="end" className="gr-chart__tick">{v}</text>
            </g>
          ))}
          {points.map((p, i) => {
            const cx = left + band * i + band / 2;
            const passed = p.washes - p.flagged;
            const gap = passed > 0 && p.flagged > 0 ? 2 : 0;
            const dim = hover !== null && hover !== i;
            return (
              <g key={p.date} opacity={dim ? 0.45 : 1}>
                {passed > 0 && <path d={colPath(cx - colW / 2, y(passed), colW, y(0) - y(passed), p.flagged === 0)} className="gr-chart__s1" />}
                {p.flagged > 0 && <path d={colPath(cx - colW / 2, y(p.washes), colW, y(passed) - y(p.washes) - gap, true)} className="gr-chart__s2" />}
                {(i % labelEvery === 0 || i === points.length - 1) && (
                  <text x={cx} y={top + plotH + 18} textAnchor="middle" className="gr-chart__tick">{fmtDay(p.date, { day: 'numeric', month: band * labelEvery > 60 ? 'short' : undefined })}</text>
                )}
                <rect x={cx - band / 2} y={top} width={band} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} />
              </g>
            );
          })}
          <line x1={left} x2={width - right} y1={y(0)} y2={y(0)} className="gr-chart__axis" />
        </svg>
      )}
      {h && hover !== null && (
        <div className="gr-chart__tip" style={{ left: Math.min(Math.max(left + band * hover + band / 2, 70), width - 70), top: Math.max(y(h.washes) - 12, 0) }}>
          <div className="gr-chart__tip-date">{fmtDay(h.date, { weekday: 'short', day: 'numeric', month: 'short' })}</div>
          <div><i className="gr-chart__key gr-chart__key--s1" /><strong>{h.washes - h.flagged}</strong> passed</div>
          <div><i className="gr-chart__key gr-chart__key--s2" /><strong>{h.flagged}</strong> flagged</div>
        </div>
      )}
    </div>
  );
}

/** Column with a 4px rounded data end (top) and a square baseline. */
function colPath(x: number, yTop: number, w: number, h: number, roundTop: boolean) {
  if (h <= 0) return '';
  const r = roundTop ? Math.min(4, w / 2, h) : 0;
  return `M${x},${yTop + h} V${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + w - r} Q${x + w},${yTop} ${x + w},${yTop + r} V${yTop + h} Z`;
}

/** Horizontal bars, one series (one colour), value at the tip. */
export function HBars({ rows, format = String }: { rows: { label: string; value: number }[]; format?: (v: number) => string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="gr-hbars">
      {rows.map((r) => (
        <li key={r.label}>
          <span className="gr-hbars__label">{r.label}</span>
          <span className="gr-hbars__track"><span className="gr-hbars__bar" style={{ width: `${(r.value / max) * 100}%` }} /><span className="gr-hbars__value">{format(r.value)}</span></span>
        </li>
      ))}
    </ul>
  );
}

const STATUS_TONE: Record<string, string> = { Approved: 'success', Verified: 'success', Flagged: 'warning', Rejected: 'error' };

/** Part-to-whole status split: one stacked bar with 2px gaps + a legend with counts (icon-free statuses carry text labels). */
export function StatusSplit({ byStatus }: { byStatus: Record<string, number> }) {
  const rows = Object.entries(byStatus).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const total = rows.reduce((s, [, v]) => s + v, 0);
  if (!total) return <p className="gr-muted" style={{ margin: 0 }}>No washes in this range.</p>;
  return (
    <div>
      <div className="gr-split" role="img" aria-label={rows.map(([k, v]) => `${k} ${v}`).join(', ')}>
        {rows.map(([k, v]) => <span key={k} className={`gr-split__seg gr-split__seg--${STATUS_TONE[k] ?? 'muted'}`} style={{ flexGrow: v }} title={`${k}: ${v}`} />)}
      </div>
      <ul className="gr-legend">
        {rows.map(([k, v]) => (
          <li key={k}><i className={`gr-split__seg--${STATUS_TONE[k] ?? 'muted'}`} />{k} <strong>{v}</strong> <span className="gr-muted">{Math.round((v / total) * 100)}%</span></li>
        ))}
      </ul>
    </div>
  );
}
